import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { API_BASE_URL } from "../config";

const TrikotDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [trikot, setTrikot] = useState(null);
  const [bidAmount, setBidAmount] = useState(""); // Keep this empty initially
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const navigate = useNavigate();

  const fetchTrikot = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/trikots/${id}`);
      setTrikot(response.data);
      const data = response.data;
      const minBid =
        !data.currentBid || data.currentBid === (data.startingBid ?? 60)
          ? 60
          : data.currentBid + 10;
      setBidAmount(minBid.toFixed(2));
    } catch (error) {
      setError(
        "Fehler beim Laden des Trikots: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTrikot();
  }, [fetchTrikot]);

  const handleBid = async (e) => {
    e.preventDefault();

    // Mindestgebot: erstes Gebot 60€, danach aktuelles Gebot + 10€
    const minBid =
      !trikot.currentBid || trikot.currentBid === (trikot.startingBid ?? 60)
        ? 60
        : trikot.currentBid + 10;
    const bidValue = parseFloat(bidAmount);
    if (bidValue < minBid) {
      setError(
        minBid === 60
          ? "Das erste Gebot muss mindestens 60 € betragen."
          : `Das Gebot muss mindestens ${minBid.toFixed(2)} € betragen (10 € über aktuellem Gebot).`
      );
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API_BASE_URL}/api/bids`,
        {
          trikotId: id,
          amount: bidValue,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      setSuccess("Gebot erfolgreich abgegeben!");
      fetchTrikot(); // Refresh trikot data
      setBidAmount(""); // Clear bid input

      setTimeout(() => {
        navigate("/my-bids"); // Redirect to "Meine Gebote"
      }, 1500); // Wait 1.5 seconds before redirecting
    } catch (error) {
      setError(
        "Fehler beim Bieten: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  if (loading) {
    return (
      <Container
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (!trikot) {
    return (
      <Container>
        <Alert severity="error">Trikot nicht gefunden</Alert>
      </Container>
    );
  }

  const isAuctionEnded = new Date(trikot.endTime) <= new Date();

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Trikot #{trikot.number} - {trikot.playerName}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Aktuelles Höchstgebot: {trikot.currentBid}€
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Auktion endet am: {new Date(trikot.endTime).toLocaleString("de-DE")}
          </Typography>
        </Box>

        {!isAuctionEnded && user && (
          <Box component="form" onSubmit={handleBid} sx={{ mt: 2 }}>
            <TextField
              label="Ihr Gebot (€)"
              type="number"
              value={bidAmount}
              onChange={(e) => setBidAmount(e.target.value)}
              inputProps={{
                min: trikot.currentBid + 10,
                step: "1",
              }}
              helperText={`Mindestgebot: ${(trikot.currentBid + 10).toFixed(
                2
              )}€`}
              fullWidth
              sx={{ mb: 2 }}
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={
                !bidAmount || parseFloat(bidAmount) < trikot.currentBid + 10
              }
            >
              Gebot abgeben
            </Button>
          </Box>
        )}

        {isAuctionEnded && (
          <Alert severity="info">Diese Auktion ist bereits beendet.</Alert>
        )}
      </Paper>
    </Container>
  );
};

export default TrikotDetail;
